import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import DashboardLayout from '../components/DashboardLayout';
import {
  User,
  Building,
  Music,
  Phone,
  DollarSign,
  ExternalLink,
  Save,
  CreditCard
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const { api } = useApi();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();

  useEffect(() => {
    if (user) {
      setValue('bandName', user.bandName || '');
      setValue('schoolName', user.schoolName || '');
      setValue('directorName', user.directorName || '');
      setValue('phone', user.phone || '');
      setValue('pricePerVideo', user.pricePerVideo || '');
      setValue('description', user.description || '');
    }
  }, [user, setValue]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    const result = await updateProfile(data);
    setIsLoading(false);
    
    if (result.success) {
      // Profile updated successfully
    }
  };

  const handleStripeOnboarding = async () => {
    try {
      const response = await api.get('/auth/stripe-onboarding');
      window.open(response.data.data.url, '_blank');
    } catch (error) {
      console.error('Failed to get Stripe onboarding URL:', error);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <LoadingSpinner size="xl" text="Loading profile..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Band Profile</h1>
            <p className="text-gray-600">Manage your band information and settings</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Profile Form */}
          <div className="lg:col-span-2">
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Band Information</h2>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Band Name */}
                  <div>
                    <label htmlFor="bandName" className="form-label">
                      Band Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Music className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="bandName"
                        type="text"
                        className={`input pl-10 ${errors.bandName ? 'input-error' : ''}`}
                        placeholder="e.g., Lincoln High Marching Band"
                        {...register('bandName', {
                          required: 'Band name is required',
                          minLength: {
                            value: 2,
                            message: 'Band name must be at least 2 characters',
                          },
                        })}
                      />
                    </div>
                    {errors.bandName && (
                      <p className="form-error">{errors.bandName.message}</p>
                    )}
                  </div>

                  {/* School Name */}
                  <div>
                    <label htmlFor="schoolName" className="form-label">
                      School Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Building className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="schoolName"
                        type="text"
                        className={`input pl-10 ${errors.schoolName ? 'input-error' : ''}`}
                        placeholder="e.g., Lincoln High School"
                        {...register('schoolName', {
                          required: 'School name is required',
                          minLength: {
                            value: 2,
                            message: 'School name must be at least 2 characters',
                          },
                        })}
                      />
                    </div>
                    {errors.schoolName && (
                      <p className="form-error">{errors.schoolName.message}</p>
                    )}
                  </div>

                  {/* Director Name */}
                  <div>
                    <label htmlFor="directorName" className="form-label">
                      Director Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="directorName"
                        type="text"
                        className={`input pl-10 ${errors.directorName ? 'input-error' : ''}`}
                        placeholder="e.g., John Smith"
                        {...register('directorName', {
                          required: 'Director name is required',
                          minLength: {
                            value: 2,
                            message: 'Director name must be at least 2 characters',
                          },
                        })}
                      />
                    </div>
                    {errors.directorName && (
                      <p className="form-error">{errors.directorName.message}</p>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label htmlFor="phone" className="form-label">
                      Phone Number
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="phone"
                        type="tel"
                        className={`input pl-10 ${errors.phone ? 'input-error' : ''}`}
                        placeholder="+1 (555) 123-4567"
                        {...register('phone', {
                          pattern: {
                            value: /^\+?[\d\s\-()]+$/,
                            message: 'Please enter a valid phone number',
                          },
                        })}
                      />
                    </div>
                    {errors.phone && (
                      <p className="form-error">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                {/* Price per Video */}
                <div>
                  <label htmlFor="pricePerVideo" className="form-label">
                    Price per Video
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="pricePerVideo"
                      type="number"
                      step="0.01"
                      min="0"
                      className={`input pl-10 ${errors.pricePerVideo ? 'input-error' : ''}`}
                      placeholder="50.00"
                      {...register('pricePerVideo', {
                        required: 'Price is required',
                        min: {
                          value: 0,
                          message: 'Price must be positive',
                        },
                      })}
                    />
                  </div>
                  {errors.pricePerVideo && (
                    <p className="form-error">{errors.pricePerVideo.message}</p>
                  )}
                  <p className="form-help">
                    This is the amount families will pay for each personalized video.
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="form-label">
                    Band Description
                  </label>
                  <textarea
                    id="description"
                    rows={4}
                    className={`input ${errors.description ? 'input-error' : ''}`}
                    placeholder="Tell families about your band and what makes your videos special..."
                    {...register('description', {
                      maxLength: {
                        value: 500,
                        message: 'Description must be less than 500 characters',
                      },
                    })}
                  />
                  {errors.description && (
                    <p className="form-error">{errors.description.message}</p>
                  )}
                  <p className="form-help">
                    This will be shown to families when they're ordering videos.
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? (
                      <LoadingSpinner size="sm" text="" />
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Payment Setup */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Setup</h3>
              
              {user.stripeAccountId ? (
                <div className="space-y-3">
                  <div className="flex items-center text-success-600">
                    <CreditCard className="w-5 h-5 mr-2" />
                    <span className="text-sm font-medium">Stripe Connected</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Your Stripe account is set up and ready to receive payments.
                  </p>
                  <button
                    onClick={handleStripeOnboarding}
                    className="btn-outline w-full"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Manage Stripe Account
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center text-warning-600">
                    <CreditCard className="w-5 h-5 mr-2" />
                    <span className="text-sm font-medium">Payment Setup Required</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Set up your Stripe account to receive payments from families.
                  </p>
                  <button
                    onClick={handleStripeOnboarding}
                    className="btn-primary w-full"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Set Up Stripe Account
                  </button>
                </div>
              )}
            </div>

            {/* Account Info */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Email</p>
                  <p className="text-sm text-gray-900">{user.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Account Type</p>
                  <p className="text-sm text-gray-900 capitalize">{user.role}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Member Since</p>
                  <p className="text-sm text-gray-900">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Profile; 